import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWhatsAppTemplates } from '../../hooks/useWhatsAppTemplates';
import { useSalon } from '../../contexts/SalonContext';
import useWindowSize from '../../hooks/useWindowSize';
import TemplateCard from './TemplateCard';
import TemplateForm from './TemplateForm';
import TemplatePreview from './TemplatePreview';
import { createDefaultTemplates } from '../../firebase/templates';
import { mapBusinessTypeToCode } from '../../utils/templateUtils';
import TemplateDetail from './TemplateDetail';

/**
 * Componente principal para gerenciar templates de WhatsApp
 * Interface moderna inspirada no Instagram/X
 * 
 * @returns {JSX.Element} Componente React
 */
const WhatsAppTemplatesManager = () => {
  const { salonId, salonConfig } = useSalon();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const filtersRef = useRef(null);
  
  const { 
    templates, 
    loading, 
    error, 
    addTemplate, 
    updateTemplate, 
    deleteTemplate,
    processTemplate,
    refreshTemplates
  } = useWhatsAppTemplates(salonId);

  // Estados locais para gerenciar a interface
  const [selectedType, setSelectedType] = useState('todos');
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [detailPreview, setDetailPreview] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Filtrar templates por tipo
  useEffect(() => {
    if (templates.length === 0) {
      setFilteredTemplates([]);
      return;
    }
    
    if (selectedType === 'todos') {
      setFilteredTemplates(templates);
    } else {
      setFilteredTemplates(templates.filter(t => t.type === selectedType));
    }
  }, [templates, selectedType]);
  
  // Função para abrir modal de edição
  const handleEdit = useCallback((template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  }, []);
  
  // Função para abrir modal de criação
  const handleCreate = useCallback(() => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  }, []);
  
  // Função para fechar modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  }, []);
  
  // Função para salvar template (criar ou atualizar)
  const handleSave = useCallback(async (formData) => {
    const success = editingTemplate
      ? await updateTemplate(editingTemplate.id, formData)
      : await addTemplate(formData);
    
    if (success) {
      handleCloseModal();
    }
  }, [editingTemplate, updateTemplate, addTemplate, handleCloseModal]);
  
  // Função para importar templates padrão
  const handleImportDefaultTemplates = useCallback(async () => {
    if (!salonId || !salonConfig) return;
    
    if (templates.length > 0 && !window.confirm(
      'Você já possui templates cadastrados. Deseja importar templates adicionais baseados no seu tipo de negócio?'
    )) {
      return;
    }
    
    setIsImporting(true);
    
    try {
      const businessType = mapBusinessTypeToCode(
        salonConfig.businessType || 'Salão de Beleza'
      );
      
      const success = await createDefaultTemplates(
        salonId, 
        businessType, 
        salonConfig.name || 'Meu Estabelecimento'
      );
      
      if (success) {
        alert('Templates padrão importados com sucesso!');
        refreshTemplates();
      } else {
        alert('Não foi possível importar os templates padrão. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao importar templates padrão:', error);
      alert('Erro ao importar templates padrão. Tente novamente.');
    } finally {
      setIsImporting(false);
    }
  }, [salonId, salonConfig, templates.length, refreshTemplates]);
  
  // Função para visualizar detalhes de um template
  const handleViewDetails = useCallback((template) => {
    setViewingTemplate(template);
    setDetailPreview(processTemplate(template.message));
  }, [processTemplate]);
  
  // Função para fechar modal de visualização detalhada
  const handleCloseDetails = useCallback(() => {
    setViewingTemplate(null);
    setDetailPreview('');
  }, []);
  
  // Tipos de templates disponíveis para filtragem
  const templateTypes = [
    { value: 'todos', label: 'Todos' },
    { value: 'confirmacao', label: 'Confirmação' },
    { value: 'lembrete', label: 'Lembrete' },
    { value: 'agradecimento', label: 'Agradecimento' },
    { value: 'cancelamento', label: 'Cancelamento' },
    { value: 'reagendamento', label: 'Reagendamento' },
    { value: 'aniversario', label: 'Aniversário' },
    { value: 'promocao', label: 'Promoção' }
  ];
  
  // Determinar quais filtros mostrar diretamente e quais colocar no dropdown "Mais"
  const visibleFilters = isMobile ? 2 : 4; // Ajuste esse valor conforme necessário
  const mainFilters = templateTypes.slice(0, visibleFilters);
  const moreFilters = templateTypes.slice(visibleFilters);
  
  // Renderizar estado de carregamento
  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Renderizar estado de erro
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        <strong className="font-bold">Erro! </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }
  
  return (
    <div className="pb-6">
      {/* Cabeçalho com título e botões de ação */}

      
      {/* Filtros/Tabs de tipos - Versão responsiva */}
      <div className="relative mb-6 mt-6">
        <div className="flex flex-wrap items-center gap-2" ref={filtersRef}>
          {/* Botões principais sempre visíveis */}
          {mainFilters.map(type => (
            <button
              key={type.value}
              onClick={() => setSelectedType(type.value)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                selectedType === type.value
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {type.label}
            </button>
          ))}
          
          {/* Botão "Mais" para exibir dropdown */}
          {moreFilters.length > 0 && (
            <div className="relative inline-block">
              <button
                onClick={() => setShowMoreFilters(!showMoreFilters)}
                className="px-4 py-2 text-sm font-medium rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center"
              >
                Mais
                <svg 
                  className={`ml-1 h-4 w-4 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown para opções adicionais */}
              {showMoreFilters && (
                <div className="absolute z-10 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <div className="py-1" role="menu" aria-orientation="vertical">
                    {moreFilters.map(type => (
                      <button
                        key={type.value}
                        onClick={() => {
                          setSelectedType(type.value);
                          setShowMoreFilters(false);
                        }}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          selectedType === type.value
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        role="menuitem"
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Lista de templates */}
      {filteredTemplates.length === 0 ? (
        <div className="py-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-4 4z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum template encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {templates.length === 0 
              ? 'Não existem templates padrão para este tipo de negócio.' 
              : 'Não há templates para o filtro selecionado.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => handleEdit(template)}
              onView={() => handleViewDetails(template)}
            />
          ))}
        </div>
      )}
      
      {/* Modal de edição/criação */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay de fundo */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleCloseModal}
            ></div>
            
            {/* Container do modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg md:max-w-xl lg:max-w-4xl w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900" id="modal-title">
                    Editar Mensagem de {templateTypes.find(t => t.value === editingTemplate?.type)?.label || 'Template'}
                  </h3>
                  
                  <button
                    onClick={handleCloseModal}
                    className="rounded-full p-1 hover:bg-gray-100"
                  >
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <TemplateForm
                  initialData={editingTemplate}
                  onSave={handleSave}
                  onCancel={handleCloseModal}
                  processTemplate={processTemplate}
                  allTemplates={templates}
                  isMobile={isMobile}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de visualização detalhada */}
      {viewingTemplate && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay de fundo */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleCloseDetails}
            ></div>
            
            {/* Container do modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <TemplateDetail 
                  template={viewingTemplate}
                  previewMessage={detailPreview}
                  onEdit={() => {
                    handleCloseDetails();
                    handleEdit(viewingTemplate);
                  }}
                  onClose={handleCloseDetails}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Fechar dropdown ao clicar fora */}
      {showMoreFilters && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMoreFilters(false)}
        ></div>
      )}
    </div>
  );
};

export default WhatsAppTemplatesManager; 